import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  Plus, Trash2, Search, Loader2, Edit2, X, Save,
  ClipboardList, IndianRupee, Receipt, CheckSquare, Square,
} from "lucide-react";
import ToothPicker from "./ToothPicker";

const STATUS_STYLES = {
  Advised: "bg-slate-100 text-slate-700",
  "In Progress": "bg-blue-100 text-blue-700",
  Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
};

const AdvisedTreatments = ({ patientId, onBillSelected }) => {
  const [catalog, setCatalog] = useState([]);
  const [advised, setAdvised] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [custom, setCustom] = useState({ name: "", fee: "" });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [checked, setChecked] = useState({}); // id -> bool

  const load = async () => {
    setLoading(true);
    try {
      const [cat, adv] = await Promise.all([
        api.get("/treatments"),
        api.get(`/patients/${patientId}/treatments`),
      ]);
      setCatalog(cat.data);
      setAdvised(adv.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (patientId) load(); /* eslint-disable-next-line */ }, [patientId]);

  const filteredCatalog = useMemo(() => {
    if (!q.trim()) return catalog;
    const s = q.trim().toLowerCase();
    return catalog.filter((t) => t.name.toLowerCase().includes(s));
  }, [q, catalog]);

  // Map of tooth -> true if any advised treatment (non-Cancelled) exists on that tooth
  const toothMarked = useMemo(() => {
    const m = {};
    advised.forEach((a) => {
      if (a.tooth && a.status !== "Cancelled") m[a.tooth] = true;
    });
    return m;
  }, [advised]);

  const addToPlan = async (t) => {
    try {
      const res = await api.post(`/patients/${patientId}/treatments`, {
        name: t.name,
        fee: t.fee,
        tooth: selectedTooth || null,
      });
      setAdvised((prev) => [res.data, ...prev]);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to add");
    }
  };

  const saveCustom = async () => {
    const name = custom.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await api.post("/treatments", { name, fee: Number(custom.fee || 0) });
      setCatalog((prev) => [...prev, res.data]);
      await addToPlan(res.data);
      setCustom({ name: "", fee: "" });
      setShowCustom(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const removeCatalog = async (id) => {
    if (!window.confirm("Delete this saved custom treatment from your catalog?")) return;
    try {
      await api.delete(`/treatments/${id}`);
      setCatalog((prev) => prev.filter((t) => t.id !== id));
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const removeAdvised = async (id) => {
    if (!window.confirm("Remove this treatment from patient's plan?")) return;
    await api.delete(`/patients/${patientId}/treatments/${id}`);
    setAdvised((prev) => prev.filter((a) => a.id !== id));
    setChecked((c) => { const n = { ...c }; delete n[id]; return n; });
  };

  const changeStatus = async (a, status) => {
    try {
      const res = await api.put(`/patients/${patientId}/treatments/${a.id}`, { status });
      setAdvised((prev) => prev.map((x) => (x.id === a.id ? res.data : x)));
    } catch { /* ignore */ }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditDraft({ name: a.name, fee: a.fee, tooth: a.tooth || "", notes: a.notes || "" });
  };
  const saveEdit = async () => {
    try {
      const res = await api.put(`/patients/${patientId}/treatments/${editingId}`, {
        name: editDraft.name,
        fee: Number(editDraft.fee || 0),
        tooth: editDraft.tooth || null,
        notes: editDraft.notes,
      });
      setAdvised((prev) => prev.map((x) => (x.id === editingId ? res.data : x)));
      setEditingId(null);
    } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
  };

  const toggleCheck = (id) => setChecked((c) => ({ ...c, [id]: !c[id] }));
  const toggleAll = () => {
    const eligible = advised.filter((a) => a.status !== "Cancelled");
    const allOn = eligible.every((a) => checked[a.id]);
    const next = {};
    if (!allOn) eligible.forEach((a) => (next[a.id] = true));
    setChecked(next);
  };

  const selectedItems = advised.filter((a) => checked[a.id] && a.status !== "Cancelled");
  const selectedTotal = selectedItems.reduce((s, x) => s + Number(x.fee || 0), 0);

  const billSelected = () => {
    if (selectedItems.length === 0) return;
    // Convert selected treatments to bill items
    const items = selectedItems.map((a) => ({
      name: a.tooth ? `${a.name} — Tooth #${a.tooth}` : a.name,
      qty: 1,
      rate: Number(a.fee || 0),
      amount: Number(a.fee || 0),
    }));
    onBillSelected?.({ items, sourceTreatmentIds: selectedItems.map((a) => a.id) });
  };

  const totals = useMemo(() => {
    const sum = (list) => list.reduce((s, x) => s + (Number(x.fee) || 0), 0);
    return {
      totalPlan: sum(advised.filter((a) => a.status !== "Cancelled")),
      completed: sum(advised.filter((a) => a.status === "Completed")),
      pending: sum(advised.filter((a) => a.status === "Advised" || a.status === "In Progress")),
    };
  }, [advised]);

  if (loading) return <div className="p-8 text-center text-slate-400 text-sm">Loading treatments...</div>;

  return (
    <div className="space-y-4">
      <ToothPicker
        selected={selectedTooth}
        onSelect={setSelectedTooth}
        marked={toothMarked}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* PLAN */}
        <div className="lg:col-span-3 bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <ClipboardList size={18} className="text-[#14B8A6]" />
              <h3 className="font-semibold text-slate-800">Treatment Plan</h3>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{advised.length}</span>
            </div>
            {selectedItems.length > 0 && (
              <button
                onClick={billSelected}
                className="inline-flex items-center gap-1.5 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white text-sm px-3 py-1.5 rounded-md font-medium"
              >
                <Receipt size={14} /> Bill selected ({selectedItems.length}) — ₹{selectedTotal.toLocaleString("en-IN")}
              </button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 mb-4">
            <TotalBox label="Total" value={totals.totalPlan} accent="#0F2A5F" />
            <TotalBox label="Pending" value={totals.pending} accent="#f5b800" />
            <TotalBox label="Completed" value={totals.completed} accent="#14B8A6" />
          </div>

          {advised.length === 0 ? (
            <div className="border border-dashed border-slate-300 rounded-md py-10 text-center text-slate-500 text-sm">
              No treatments advised yet. {selectedTooth ? `Selected tooth #${selectedTooth}. ` : ""}Pick from the catalog on the right →
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 px-1">
                <button onClick={toggleAll} className="inline-flex items-center gap-1 hover:text-slate-800">
                  {advised.filter((a) => a.status !== "Cancelled").every((a) => checked[a.id]) && advised.length > 0
                    ? <CheckSquare size={13} className="text-[#14B8A6]" />
                    : <Square size={13} />}
                  <span>Select all billable</span>
                </button>
              </div>
              <ul className="divide-y divide-slate-100">
                {advised.map((a) => (
                  <li key={a.id} className="py-3">
                    {editingId === a.id ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm col-span-2" placeholder="Treatment name" />
                          <input type="number" step="0.01" value={editDraft.fee} onChange={(e) => setEditDraft((d) => ({ ...d, fee: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm" placeholder="Fee" />
                          <input value={editDraft.tooth} onChange={(e) => setEditDraft((d) => ({ ...d, tooth: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm" placeholder="Tooth (FDI)" />
                          <textarea rows={2} value={editDraft.notes} onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))} className="px-2 py-1.5 border border-slate-300 rounded text-sm col-span-2" placeholder="Notes" />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditingId(null)} className="text-xs text-slate-600 hover:bg-slate-100 px-2 py-1 rounded">Cancel</button>
                          <button onClick={saveEdit} className="text-xs bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white px-2 py-1 rounded inline-flex items-center gap-1">
                            <Save size={11} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={!!checked[a.id]}
                          onChange={() => toggleCheck(a.id)}
                          disabled={a.status === "Cancelled"}
                          className="w-4 h-4 accent-[#14B8A6] mt-0.5 flex-shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <div className="font-medium text-slate-800 text-sm">{a.name}</div>
                            {a.tooth && <span className="text-[10px] bg-[#E6F7F5] text-[#0F8A7D] px-1.5 py-0.5 rounded font-mono">#{a.tooth}</span>}
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[a.status] || STATUS_STYLES.Advised}`}>{a.status}</span>
                          </div>
                          {a.notes && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line">{a.notes}</div>}
                          <div className="mt-1.5 text-sm font-semibold text-slate-800">₹{Number(a.fee).toLocaleString("en-IN")}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <select
                            value={a.status}
                            onChange={(e) => changeStatus(a, e.target.value)}
                            className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                          >
                            <option>Advised</option>
                            <option>In Progress</option>
                            <option>Completed</option>
                            <option>Cancelled</option>
                          </select>
                          <button onClick={() => startEdit(a)} className="p-1.5 text-slate-400 hover:text-[#0F2A5F] rounded"><Edit2 size={14} /></button>
                          <button onClick={() => removeAdvised(a.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* CATALOG */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-800">Catalog</h3>
            <button
              onClick={() => setShowCustom((v) => !v)}
              className="text-xs inline-flex items-center gap-1 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white px-2 py-1 rounded"
            >
              <Plus size={12} /> Add custom
            </button>
          </div>

          {showCustom && (
            <div className="mb-3 border border-slate-200 rounded p-3 bg-slate-50">
              <div className="text-xs font-medium text-slate-600 mb-2">New Custom Treatment</div>
              <input
                value={custom.name}
                onChange={(e) => setCustom((c) => ({ ...c, name: e.target.value }))}
                placeholder="e.g. Laser Frenectomy"
                className="w-full mb-2 px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
              />
              <div className="relative mb-2">
                <IndianRupee size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="number"
                  step="0.01"
                  value={custom.fee}
                  onChange={(e) => setCustom((c) => ({ ...c, fee: e.target.value }))}
                  placeholder="Fee"
                  className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCustom(false)} className="text-xs text-slate-600 hover:bg-slate-100 px-2 py-1 rounded">Cancel</button>
                <button onClick={saveCustom} disabled={saving || !custom.name.trim()} className="text-xs bg-[#0F2A5F] hover:bg-[#0A1E4A] disabled:opacity-60 text-white px-2 py-1 rounded inline-flex items-center gap-1">
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save & add
                </button>
              </div>
            </div>
          )}

          <div className="relative mb-3">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search treatments..."
              className="w-full pl-8 pr-2 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
            />
          </div>

          {selectedTooth && (
            <div className="text-[11px] text-[#0F8A7D] bg-[#E6F7F5] border border-[#14B8A6]/30 rounded px-2 py-1 mb-2">
              Adding for tooth <span className="font-bold">#{selectedTooth}</span>. Deselect from picker to add without tooth.
            </div>
          )}

          <ul className="divide-y divide-slate-100 max-h-[520px] overflow-y-auto -mx-2 px-2">
            {filteredCatalog.map((t) => (
              <li key={t.id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 truncate">
                    {t.name}
                    {t.is_custom && <span className="ml-1.5 text-[9px] text-[#14B8A6] font-semibold uppercase">Custom</span>}
                  </div>
                  <div className="text-xs text-slate-500">₹{Number(t.fee).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {t.is_custom && (
                    <button onClick={() => removeCatalog(t.id)} className="p-1.5 text-slate-300 hover:text-red-600 rounded" title="Delete from catalog">
                      <Trash2 size={12} />
                    </button>
                  )}
                  <button
                    onClick={() => addToPlan(t)}
                    className="text-xs inline-flex items-center gap-1 bg-[#E6F7F5] text-[#0F8A7D] hover:bg-[#14B8A6] hover:text-white px-2 py-1 rounded font-medium transition-colors"
                  >
                    <Plus size={11} /> Add
                  </button>
                </div>
              </li>
            ))}
            {filteredCatalog.length === 0 && (
              <li className="py-6 text-center text-slate-400 text-sm">No treatments match "{q}"</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

const TotalBox = ({ label, value, accent }) => (
  <div className="border border-slate-200 rounded-lg p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    <div className="text-lg font-bold" style={{ color: accent }}>₹{Number(value || 0).toLocaleString("en-IN")}</div>
  </div>
);

export default AdvisedTreatments;
